import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Route, useHistory, useParams } from 'react-router';
import { Trans, useTranslation } from 'react-i18next';
import moment, { Moment } from 'moment';
import { EEventNames, pageWrapper } from '../../../../../client/analytics';
import { useAppSelector, useCanAccess } from '@common/hooks';
import { EComponentTypes, EPermissions, EPredicateFields, EPredicateOperators } from '@common/definitions';
import { Container } from '@common/components/container';
import ToolBar from '@common/components/tool-bar';
import WeekSelector from '@common/components/week-selector';
import { Button } from '@common/components/button';
import Permission from '@common/components/permission';
import Dropdown from '@common/components/dropdown';
import { TableDropdown } from '@common/components/table';
import Icon from '@common/components/icon';
import AddExchangeForm from '@modules/flexchange/forms/add-exchange/create';
import { CalendarProvider, useCalendar } from '../../context/calendar-context';
import CalendarGrid from '../../components/calendar-grid';
import {
  AnniversaryEvent,
  BirthdayEvent,
  CalendarActionTypes,
  CalendarEvent,
  EEventFilterTypes,
  EEventTypes,
  ShiftEvent,
} from '../../types/events';
import * as organisationSelector from '../../../organisation/selectors/organisation';
import * as networkSelector from '../../../network/selectors/network';
import CelebrationModel from '@modules/calendar/components/celebration-model';
import * as userSelector from '../../../core/selectors/logged-user';
import Exchange from '@modules/flexchange/components/exchange';
import { Shift } from '@modules/schedules/types/objects';
import UploadSchedule from '@modules/calendar/components/upload-schedule';
import ScheduleModel from '@modules/calendar/components/schedule-model';
import CalendarBrowser from '@modules/calendar/components/calendar-browser';
import '../../styles.scss';
import Spinner from '@common/components/spinner';
import FiltersToolbar from '@common/components/filters-toolbar/filters-toolbar';
import { EPredicateFilters } from '@common/components/predicates-filter';
import * as schedulesSelector from '@modules/schedules/selectors/schedules';
import * as R from 'ramda';
import UploadForm from '@modules/schedules/forms/upload';


function CalendarContent() {
  const { t } = useTranslation();
  const history = useHistory();
  const [showEventModal, setShowEventModal] = useState(false);
  const [isUploadScheduleModalVisible, setIsUploadScheduleModalVisible] = useState(false);
  const [, setSelectedDate] = useState<Moment | null>(null);
  const [selectedCelebration, setSelectedCelebration] =
    useState<CalendarEvent | null>(null);
  const [selectedShift, setSelectedShift] = useState<ShiftEvent | null>(null);
  const [showManualModal, setShowManualModal] = useState({
    manual: false,
    integrated: false,
  });
  const [dateSelected] = useState<Moment | null>(null);
  const [showCalendarBrowser, setShowCalendarBrowser] = useState(false);

  // Get data from Redux selectors
  const loggedUser = useAppSelector(userSelector.selected);
  const organisation = useAppSelector(organisationSelector.selected);
  const network = useAppSelector((state) => networkSelector.selected(state));
  const schedules = useAppSelector((state) => schedulesSelector.schedulesByWeek(state)
  );
  const functions = useAppSelector((state) => userSelector.functions(state, EPermissions.NETWORK_SCHEDULES_VIEW_ALL));
  const form = useAppSelector((state) => state.form['upload-schedule']);
  const { networkId, week } =
    useParams<{ networkId?: string; week?: string }>();
  const hasExternalSchedule = organisation?.integrations?.length &&
    organisation.integrations.find(
      (integration) => integration.external_schedule === true
    );

  const {
    currentWeek,
    isLoading,
    error,
    activeFilters,
    toggleEventTypeFilter,
    goToNextWeek,
    goToPreviousWeek,
    goToToday,
    fetchEvents,
    refetchSchedulesCallback,
    clearFilters,
  } = useCalendar();

  useEffect(() => {
    if (!week) {
      const currentWeekString = moment().format('YYYY-MM-DD');
      const currentPath = history.location.pathname;

      if (currentPath.includes('/admin/calendar')) {
        history.replace(`/admin/calendar/${currentWeekString}`);
      } else if (networkId) {
        history.replace(`/networks/${networkId}/calendar/${currentWeekString}`);
      }
    }
  }, [week, history, networkId]);

  useEffect(() => {
    fetchEvents(currentWeek, organisation?.id, loggedUser.id);
  }, [currentWeek, organisation?.id, fetchEvents]);

  const handleGoToWeek = useCallback(
    (action: CalendarActionTypes) => {
      switch (action) {
        case CalendarActionTypes.NOW:
          return goToToday();
        case CalendarActionTypes.NEXT:
          return goToNextWeek();
        case CalendarActionTypes.PREVIOUS:
          return goToPreviousWeek();
        default:
      }
    },
    [goToToday, goToNextWeek, goToPreviousWeek]
  );

  const handleEventClick = useCallback(
    (event: CalendarEvent) => {
      switch (event.type) {
        case EEventTypes.EXCHANGE:
          if (networkId) {
            const currentWeekString = currentWeek.format('YYYY-MM-DD');
            history.push(
              `/networks/${networkId}/calendar/${currentWeekString}/exchanges/${event.id}`
            );
          }
          break;
        case EEventTypes.SHIFT:
          setSelectedShift(event);
          break;
        case EEventTypes.EVENT:
          if (networkId) {
            history.push(`/networks/${networkId}/events/${event.id}`);
          } else {
            history.push(`/admin/events/${event.id}`);
          }
          break;
        case EEventTypes.BIRTHDAY:
        case EEventTypes.ANNIVERSARY:
          handleCelebrationClick(event);
          break;
        default:
          break;
      }
    },
    [history, network, networkId, currentWeek]
  );

  const handleDayClick = useCallback((date: Moment) => {
    setSelectedDate(date);
    setShowEventModal(true);
  }, []);

  const handleCelebrationClick = useCallback((event: CalendarEvent) => {
    if (
      (event.type === EEventTypes.BIRTHDAY ||
        event.type === EEventTypes.ANNIVERSARY) &&
      event.users.length === 1
    ) {
      if (networkId) {
        history.push(`/networks/${networkId}/users/${event.users[0].id}/about`);
      } else {
        history.push(`/admin/users/${event.users[0].id}/about`);
      }
    } else {
      setShowEventModal(true);
      setSelectedCelebration(event);
    }
  }, []);

  const handleWeekSelect = useCallback((weekParam: Moment) => {
    const weekString = weekParam.format('YYYY-MM-DD');
    if (networkId) {
      history.push(`/networks/${networkId}/calendar/${weekString}`);
    } else {
      history.push(`/admin/calendar/${weekString}`);
    }
  }, [history, networkId]);

  const handleCalendarBrowserToggle = useCallback(() => {
    setShowCalendarBrowser((prev) => !prev);
  }, []);

  if (error) {
    return (
      <Container name="Calendar">
        <Container.Content>
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Icon
                type="warning"
                className="w-12 h-12 text-red-500 mx-auto mb-4"
              />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('calendar:error_loading_calendar')}
              </h3>
              <p className="text-gray-600">{error}</p>
              <Button
                type="primary"
                onClick={() => fetchEvents(currentWeek, organisation?.id)}
                className="mt-4"
              >
                {t('calendar:try_again')}
              </Button>
            </div>
          </div>
        </Container.Content>
      </Container>
    );
  }

  const canSeeEvents = useCanAccess({
    components: EComponentTypes.EVENTS,
  });

  const canReviewExchanges = useCanAccess({
    permissions: [EPermissions.NETWORK_EXCHANGES_REVIEW, EPermissions.NETWORK_FUNCTION_MEMBERSHIPS_EXCHANGES_REVIEW,],
  });

  const canViewExchanges = useCanAccess({
    components: EComponentTypes.FLEXCHANGE,
    isOnlyNetwork: true,
  });

  const filteredOptions = useMemo(() => {
    return [
      {
        id: EEventFilterTypes.CELEBRATION,
        label: t('calendar:celebrations')
      },
      ...(canSeeEvents ? [{
        id: EEventFilterTypes.EVENT,
        label: t('calendar:event_type_events')
      }] : []),
      ...(hasExternalSchedule ? [{
        id: EEventFilterTypes.SHIFT,
        label: t('calendar:event_type_shifts')
      }] : []),
      ...(canViewExchanges ? [
        {
          id: EEventFilterTypes.MY_EXCHANGE,
          label: t('calendar:event_type_my_exchanges')
        },
        {
          id: EEventFilterTypes.OPEN_EXCHANGE,
          label: t('calendar:event_type_open_exchanges')
        },
        ...(canReviewExchanges ? [{
          id: EEventFilterTypes.PENDING_EXCHANGE,
          label:
            t('calendar:event_type_pending_exchanges')
        },
        {
          id: EEventFilterTypes.APPROVED_EXCHANGE,
          label:
            t('calendar:event_type_approved_exchanges')
        }] : []),
      ] : []),
    ];
  }, [activeFilters, hasExternalSchedule, canSeeEvents, canReviewExchanges, canViewExchanges]);

  const showShiftLabel = useMemo(() => {
    return (!hasExternalSchedule && canViewExchanges) || hasExternalSchedule;
  }, [hasExternalSchedule, canViewExchanges]);

  const [predicates, setPredicates] = useState([
    {
      filter: EPredicateFilters[EPredicateFields.CALENDAR_EVENT_TYPE],
      value: {
        attribute: EPredicateFields.CALENDAR_EVENT_TYPE,
        comparison: EPredicateOperators.IN,
        value: [],
      },
      options: filteredOptions,
    }
  ]);

  const [eventType] = useMemo(() => {
    return [
      predicates.find(
        (p) => p.filter === EPredicateFilters[EPredicateFields.CALENDAR_EVENT_TYPE]
      )?.value.value
    ];
  }, [predicates]);

  useEffect(() => {
    if (eventType && eventType.length > 0) {
      toggleEventTypeFilter(eventType);
    } else {
      clearFilters();
    }
  }, [eventType, toggleEventTypeFilter, clearFilters]);

  return (
    <>
      <Container name="Calendar">
        <Route
          path="/networks/:networkId/calendar/:week?/exchanges/:exchangeId"
          render={({ match }) => (
            <Exchange
              exchangeId={match.params.exchangeId}
              organisation={organisation}
              loggedUser={loggedUser}
              path={`calendar/${currentWeek.format('YYYY-MM-DD')}`}
            />
          )}
        />
        <Container.Content>
          <ToolBar className="!tw-px-0 !tw-w-full tw-flex-wrap tw-gap-2">
            <WeekSelector
              isBorder
              isFlexRemove
              date={currentWeek}
              min={undefined}
              max={undefined}
              onGoToPreviousWeek={() => handleGoToWeek(CalendarActionTypes.PREVIOUS)}
              onGoToToday={() => handleGoToWeek(CalendarActionTypes.NOW)}
              onGoToNextWeek={() => handleGoToWeek(CalendarActionTypes.NEXT)}
              onWeekClick={handleCalendarBrowserToggle}
              isFetching={false}
            />
            <Permission component={EComponentTypes.SCHEDULE} isOnlyNetwork>
              <div className="tw-flex tw-items-center">
                <ScheduleModel currentWeek={currentWeek} />
              </div>
            </Permission>
            <div className="tw-flex tw-items-center tw-gap-2 lg:tw-ml-auto md:tw-ml-auto sm:tw-ml-4 tw-ml-0 tw-flex-shrink-0">
              <FiltersToolbar
                predicates={predicates}
                setPredicates={setPredicates}
                searchEnabled={false}
              />
              <Permission
                component={[EComponentTypes.FLEXCHANGE, EComponentTypes.SCHEDULE]}
                isOnlyNetwork
              >
                {(!hasExternalSchedule || canViewExchanges) &&
                  <Dropdown
                    alignRight
                    toggle={
                      <Button type="primary" iconRight="add" size="default">
                        <Trans i18nKey="core:add" />
                      </Button>
                    }
                  >
                    <Permission component={EComponentTypes.SCHEDULE} isOnlyNetwork>
                      {!hasExternalSchedule &&
                        <UploadSchedule
                          currentWeek={currentWeek}
                          setIsUploadModalVisible={() => setIsUploadScheduleModalVisible(true)}
                        />}
                    </Permission>
                    <Permission
                      component={EComponentTypes.FLEXCHANGE}
                      isOnlyNetwork
                    >
                      <TableDropdown.Item
                        onClick={() => setShowManualModal((current) => ({ ...current, manual: true }))}
                      >
                        <div role="button">
                          <Icon type="sync_alt" />
                          {t('calendar:create_exchange_button')}
                        </div>
                      </TableDropdown.Item>
                    </Permission>
                  </Dropdown>}
              </Permission>
              <Permission
                name={[
                  EPermissions.NETWORK_FUNCTION_MEMBERSHIPS_SCHEDULES_CREATE,
                  EPermissions.NETWORK_SCHEDULES_CREATE,
                ]}
              >
                {isUploadScheduleModalVisible && (
                  <UploadForm
                    show={isUploadScheduleModalVisible}
                    key="form"
                    {...({ onClose: () => setIsUploadScheduleModalVisible(false) } as any)}
                    network={network}
                    functions={functions}
                    schedules={R.filter(
                      R.has('id'),
                      R.flatten(R.pluck('data', schedules as any[]))
                    )}
                    formValues={form?.values}
                    refetchSchedules={refetchSchedulesCallback}
                  />
                )}
              </Permission>
            </div>
          </ToolBar>

          {/* Calendar Grid */}
          {isLoading ? <Spinner centered size="large" /> :
          <CalendarGrid
            onEventClick={handleEventClick}
            onDayClick={handleDayClick}
            showShiftLabel={showShiftLabel as boolean}
            canSeeEvents={canSeeEvents as boolean}
          />}
        </Container.Content>
      </Container>
      {showEventModal && (
        <CelebrationModel
          selectedCelebration={
            selectedCelebration as BirthdayEvent | AnniversaryEvent
          }
          show={showEventModal}
          onClose={() => setShowEventModal(false)}
        />
      )}
      <AddExchangeForm
        show={showManualModal.manual}
        schedulesIntegrated={false}
        date={dateSelected || undefined}
        onClose={() => setShowManualModal((current) => ({ ...current, manual: false }))}
      />
      <AddExchangeForm
        show={!!selectedShift}
        shift={selectedShift as Shift | undefined}
        schedulesIntegrated
        onClose={() => {
          setTimeout(() => {
            setSelectedShift(null);
          }, 200);
        }}
      />
      <CalendarBrowser
        currentWeek={currentWeek}
        onWeekSelect={handleWeekSelect}
        onClose={() => setShowCalendarBrowser(false)}
        isOpen={showCalendarBrowser}
      />
    </>
  );
}

const Calendar = () => {
  return (
    <CalendarProvider>
      <CalendarContent />
    </CalendarProvider>
  );
};

export default pageWrapper(EEventNames.VISITED_CALENDAR_PAGE)(Calendar);
